import { useEditorSession } from '../../../hooks/useEditorSession';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { CloseIcon, LocateIcon } from '../../../components/ui/icons';
import { DialogContent } from '../../../components/ui/DialogContent';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GpsCoordinates,
  isValidGps,
  roundGpsTo2Decimals,
} from '../../../domain/location';
import { ResponsiveDialog } from '../../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../../components/ui/DialogHeader';
import { DialogActions } from '../../../components/ui/DialogActions';
import { collectionManagementErrorMessage } from '../collectionManagementService';

export function CollectionGpsDialog({
  open,
  collectionName,
  gps,
  onClose,
  onSubmit,
}: {
  open: boolean;
  collectionName: string;
  gps?: GpsCoordinates;
  onClose: () => void;
  onSubmit: (gps: GpsCoordinates | null) => Promise<void>;
}) {
  const baseline = useEditorSession(open ? 'gps' : null, { gps });
  const initialGps = baseline?.gps;
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locationRequest = useRef(0);

  useEffect(
    () => () => {
      locationRequest.current += 1;
    },
    [open],
  );

  const close = () => {
    locationRequest.current += 1;
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setLatitude('');
      setLongitude('');
      setGettingLocation(false);
      setBusy(false);
      setError(null);
      return;
    }
    setLatitude(
      initialGps?.latitude !== undefined ? initialGps.latitude.toFixed(2) : '',
    );
    setLongitude(
      initialGps?.longitude !== undefined
        ? initialGps.longitude.toFixed(2)
        : '',
    );
  }, [open, initialGps]);

  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    scopeKey: 'app.child',
    open,
    pending: busy,
    dirty:
      latitude !== (initialGps?.latitude.toFixed(2) || '') ||
      longitude !== (initialGps?.longitude.toFixed(2) || ''),
    onDiscard: () => {
      locationRequest.current += 1;
      setLatitude('');
      setLongitude('');
    },
    onClose: close,
  });
  const requestDialogClose = () => {
    void requestClose();
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setGettingLocation(true);
    setError(null);
    const request = ++locationRequest.current;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (request !== locationRequest.current) return;
        const rounded = roundGpsTo2Decimals({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLatitude(rounded.latitude.toFixed(2));
        setLongitude(rounded.longitude.toFixed(2));
        setGettingLocation(false);
      },
      (geoError) => {
        if (request !== locationRequest.current) return;
        setGettingLocation(false);
        setError(`Could not fetch location: ${geoError.message}`);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const clear = () => {
    setLatitude('');
    setLongitude('');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || gettingLocation) return;
    const latTrimmed = latitude.trim();
    const lonTrimmed = longitude.trim();
    if (!latTrimmed && !lonTrimmed) {
      if (gps === undefined) {
        setError(
          'This collection has no GPS location yet. Enter coordinates or use Current GPS.',
        );
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await onSubmit(null);
        closeAfterSave();
      } catch (err) {
        setError(collectionManagementErrorMessage(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!latTrimmed || !lonTrimmed) {
      setError(
        'Both latitude and longitude are required if specifying a GPS location.',
      );
      return;
    }
    const candidate = {
      latitude: Number(latTrimmed),
      longitude: Number(lonTrimmed),
    };
    if (!isValidGps(candidate)) {
      setError(
        'Enter a valid GPS location (latitude -90 to 90, longitude -180 to 180).',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(roundGpsTo2Decimals(candidate));
      closeAfterSave();
    } catch (err) {
      setError(collectionManagementErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={requestDialogClose}
      aria-labelledby="collection-gps-dialog-title"
      fullScreenOnPhone
    >
      <DialogHeader
        id="collection-gps-dialog-title"
        title={`Edit GPS location`}
        onClose={requestDialogClose}
      />
      <DialogContent>
        <Stack
          component="form"
          id="collection-gps-form"
          onSubmit={(event) => {
            void submit(event);
          }}
          spacing={2}
        >
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {collectionName}
          </Typography>
          <Box
            sx={{
              p: 1,
              px: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.75 }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                GPS Location (~1km area, 2 decimals)
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  startIcon={<LocateIcon fontSize="small" />}
                  onClick={handleGetCurrentLocation}
                  disabled={gettingLocation || busy}
                  sx={{ py: 0.25, px: 0.75, fontSize: '0.75rem' }}
                >
                  {gettingLocation ? 'Locating…' : 'Current GPS'}
                </Button>
                {(latitude || longitude) && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={clear}
                    disabled={gettingLocation || busy}
                    sx={{ py: 0.25, px: 0.5, fontSize: '0.75rem' }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="Lat"
                placeholder="-36.85"
                value={latitude}
                onChange={(event) => setLatitude(event.target.value)}
                disabled={busy || gettingLocation}
                fullWidth
                inputProps={{
                  style: { padding: '6px 8px', fontSize: '0.875rem' },
                }}
              />
              <TextField
                size="small"
                label="Lng"
                placeholder="174.76"
                value={longitude}
                onChange={(event) => setLongitude(event.target.value)}
                disabled={busy || gettingLocation}
                fullWidth
                inputProps={{
                  style: { padding: '6px 8px', fontSize: '0.875rem' },
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestDialogClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="collection-gps-form"
          variant="contained"
          disabled={busy || gettingLocation}
        >
          {busy ? 'Saving…' : 'Save GPS'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
